import { useState } from 'react';
import { DATA_TABLE_CELLS, DATA_TABLE_HEADER } from '@/lib/data-table';
import { ExternalLink, Gavel } from 'lucide-react';
import {
  Badge,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@staff-app/ui';
import {
  useDecideDunningReview,
  useReactivateDunningAccount,
  useDunningReviewAccess,
  useDunningReviewQueue,
  useDunningSuspendedList,
  useDunningSweepAborts,
} from '../hooks/useDunningReview';
import { StripeExclusionsSection } from '../components/StripeExclusionsSection';
import { DeactivateDialog } from '../components/DeactivateDialog';
import { SweepAbortBanner } from '../components/SweepAbortBanner';
import {
  AccountStatusBadge,
  DunningFlagBadge,
  SubscriptionStatusBadge,
} from '../components/StatusBadges';
import { PagerNav, PagerSummary, useClientPage } from '../components/TablePager';
import {
  DunningFilterBar,
  DEFAULT_FILTERS,
  applyDunningFilters,
  type DunningFilterState,
} from '../components/DunningFilters';
import { CopyBtn } from '@/features/customers/components/CustomerFacts';
import { Link } from 'react-router';
import type { DunningReviewRow } from '@staff-app/shared-types';

/**
 * The dunning ladder's terminal rung, as a page.
 *
 * Both diagrams end their 25+ arm at "manager review tab — manager decides, no
 * auto shutoff". That last clause is the whole design: **nothing here escalates on
 * a timer.** An account sits in this queue, still playing, until a human decides —
 * so an empty-looking day is not the queue draining, it is nobody having decided.
 *
 * There is now exactly ONE decision: **Deactivate** (labelled "Turn off screens" until
 * 2026-09-23 — requester). (The old "Keep screens
 * on" reprieve was removed — only payment brings an account back.) Switching off
 * darkens a display in someone's lobby AND locks the account, so it asks for
 * confirmation and a reason, and the reason is stored against the episode. It is
 * not a cure: only payment clears an episode and reverses the switch-off.
 */
function ladderLabel(ladder: string): string {
  return ladder === 'po' ? 'Invoice / net terms' : 'Card on file';
}

/**
 * The account cell shared by the review queue and the switched-off list: the name (linking to
 * the customer's page) and the STRIPE customer id with a copy button — not the OptiSigns account
 * id (requester, 2026-09-23), matching the Dunning Exclusion List.
 */
function AccountIdentity({ row }: { row: DunningReviewRow }) {
  const name = row.companyName ?? row.stripeId ?? row.accountId;
  return (
    <>
      <Link
        to={`/staff/customers/${row.accountId}`}
        // One line; a long name ends in "…" and the full name is on hover (requester, 2026-09-23).
        className="block max-w-[240px] truncate font-medium text-foreground underline-offset-2 hover:underline"
        title={name}
        data-testid={`account-name-${row.accountId}`}
      >
        {name}
      </Link>
      {row.stripeId ? (
        <div className="font-mono text-xs text-muted-foreground">
          <span data-testid={`account-stripe-id-${row.accountId}`}>{row.stripeId}</span>
          <CopyBtn
            value={row.stripeId}
            label="Stripe ID"
            testId={`account-stripe-copy-${row.accountId}`}
          />
        </div>
      ) : (
        <div
          className="text-xs text-muted-foreground"
          data-testid={`account-stripe-id-${row.accountId}`}
        >
          {'No Stripe customer'}
        </div>
      )}
    </>
  );
}

/** Where a row is: waiting on a manager (`review`) or already deactivated (`suspended`). */
type Stage = 'review' | 'suspended';

/**
 * One row of the combined table (requester, 2026-09-23 — the review queue and the Deactivated
 * accounts list are ONE table now). A `review` row offers Deactivate; a `suspended` row offers
 * Reactivate, which restores access and puts the account back to awaiting a decision.
 */
function DunningRow({
  row,
  stage,
  onDecide,
  onReactivate,
  busy,
}: {
  row: DunningReviewRow;
  stage: Stage;
  onDecide: () => void;
  onReactivate: () => void;
  busy: boolean;
}) {
  const deactivated = stage === 'suspended';
  return (
    <TableRow data-testid={`${deactivated ? 'suspended' : 'review'}-row-${row.accountId}`}>
      <TableCell>
        <AccountIdentity row={row} />
      </TableCell>
      <TableCell data-testid={`stage-${row.accountId}`}>
        {deactivated ? (
          <Badge variant="destructive">{'Deactivated'}</Badge>
        ) : (
          <Badge variant="warning">{'Awaiting decision'}</Badge>
        )}
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">{ladderLabel(row.ladder)}</TableCell>
      <TableCell className="text-right text-sm tabular-nums">{row.screenCount ?? '—'}</TableCell>
      <TableCell className="text-right text-sm tabular-nums">{row.daysInDunning}</TableCell>
      <TableCell className="text-sm">
        {row.stripeId ? (
          <a
            href={`https://dashboard.stripe.com/customers/${row.stripeId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-primary hover:underline"
            data-testid={`stripe-link-${row.accountId}`}
          >
            {'Stripe'}
            <ExternalLink className="h-3 w-3" />
          </a>
        ) : (
          <span className="text-muted-foreground" title="No Stripe customer on this account">
            {'—'}
          </span>
        )}
      </TableCell>
      <TableCell>
        <AccountStatusBadge
          hasAccount
          isActive={row.isActive}
          isTrial={row.isTrial}
          testId={`review-account-status-${row.accountId}`}
        />
      </TableCell>
      <TableCell>
        {row.stripeId ? (
          <SubscriptionStatusBadge
            status={row.subscriptionStatus}
            multiple={row.subscriptionMultiple}
            testId={`review-subscription-status-${row.accountId}`}
          />
        ) : (
          <span
            className="text-muted-foreground"
            data-testid={`review-subscription-status-${row.accountId}`}
          >
            {'—'}
          </span>
        )}
      </TableCell>
      {/* Dunning status = the Billing Console flag on the Stripe customer (requester,
          2026-09-23) — it replaced the Hold column, which nothing could set. Note staff-app's
          own ladder does not read this flag; another tool does. */}
      <TableCell>
        <DunningFlagBadge
          value={row.dunningExclusion}
          untilDate={row.dunningExclusionUntil}
          testId={`review-dunning-status-${row.accountId}`}
        />
      </TableCell>
      <TableCell>
        <div className="flex justify-end">
          {/* Deactivate darkens a lobby display and locks the account; Reactivate undoes it
              (back to awaiting a decision). Both confirm and take a reason (the caller). */}
          {deactivated ? (
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={onReactivate}
              data-testid={`reactivate-${row.accountId}`}
            >
              {'Reactivate'}
            </Button>
          ) : (
            <Button
              size="sm"
              variant="destructive"
              disabled={busy}
              onClick={onDecide}
              data-testid={`suspend-${row.accountId}`}
            >
              {'Deactivate'}
            </Button>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

export function DunningReviewPage() {
  const { canDecide, loading: accessLoading } = useDunningReviewAccess();
  const { rows, loading, error } = useDunningReviewQueue();
  // An open abort means the queue below may be stale rather than genuinely empty.
  const { aborts } = useDunningSweepAborts();
  const { rows: suspended } = useDunningSuspendedList();
  // ONE table: awaiting-decision rows first (most overdue first, as the server orders them),
  // then the deactivated ones.
  const combined = [
    ...rows.map((row) => ({ row, stage: 'review' as Stage })),
    ...suspended.map((row) => ({ row, stage: 'suspended' as Stage })),
  ];
  // Filters above the table (requester, 2026-09-23). The pager resets to page 1 whenever
  // they change, so a narrowed result never opens on an empty page.
  // Opens on Stage = Awaiting decision (DEFAULT_FILTERS).
  const [filters, setFilters] = useState<DunningFilterState>(DEFAULT_FILTERS);
  const filtered = applyDunningFilters(combined, filters);
  const page = useClientPage(filtered, JSON.stringify(filters));
  const { decide, deciding } = useDecideDunningReview();
  const { reactivate, reactivating } = useReactivateDunningAccount();
  const [reactivatingRow, setReactivatingRow] = useState<DunningReviewRow | null>(null);

  const confirmReactivate = async (row: DunningReviewRow, reason: string) => {
    const who = row.companyName ?? row.accountId;
    setPending(row.accountId);
    try {
      const out = await reactivate(row.accountId, reason);
      // Same wording as Force Refresh: it says whether the screen push actually landed.
      setNotice(
        out.applied
          ? `${who}: ${out.message ?? 'reactivated and awaiting a decision again.'}`
          : `${who} was no longer deactivated — nothing changed. It may have just been paid.`,
      );
    } catch (e) {
      throw new Error(`Could not reactivate ${who}: ${(e as Error).message}`);
    } finally {
      setPending(null);
    }
  };
  const [pending, setPending] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // The row whose Deactivate dialog is open (null = closed). The dialog replaced the
  // browser confirm + prompt pair, so it looks like every other dialog in the console.
  const [confirming, setConfirming] = useState<DunningReviewRow | null>(null);

  const handleDecide = (row: DunningReviewRow) => setConfirming(row);

  // Called by the dialog with the typed reason. Throws on failure so the dialog stays open
  // and shows the message; the page-level notice reports the outcome once it closes.
  const confirmDeactivate = async (row: DunningReviewRow, reason: string) => {
    const who = row.companyName ?? row.accountId;
    setPending(row.accountId);
    try {
      // The reason is stored against the episode, so "why was this account switched
      // off" is part of the audit trail.
      const applied = await decide(row.accountId, reason);
      setNotice(
        applied
          ? `${who}: deactivated — screens turned off and account locked.`
          : `${who} was no longer awaiting review — nothing changed. It may have just been paid.`,
      );
    } catch (e) {
      throw new Error(`Could not deactivate ${who}: ${(e as Error).message}`);
    } finally {
      setPending(null);
    }
  };

  if (accessLoading) return null;

  if (!canDecide) {
    return (
      <div className="px-6 py-10 text-sm text-muted-foreground" data-testid="dunning-review-denied">
        {
          'Dunning Review is available to root and billing users only. Ask one of them if you need a review actioned.'
        }
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col overflow-hidden" data-testid="dunning-review-page">
      <div className="px-6 pt-6">
        <h1 className="flex items-center gap-2 text-2xl font-bold text-foreground">
          <Gavel className="h-6 w-6" />
          {'Dunning Review'}
        </h1>
        <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
          {
            'Accounts that reached the end of the past-due ladder, and the ones already deactivated. Nothing here switches off on a timer: "Awaiting decision" accounts keep playing until a manager deactivates them, and a deactivated account comes back when it pays — or when a manager reactivates it.'
          }
        </p>
      </div>

      {aborts.length ? (
        <div className="mx-6 mt-3">
          <SweepAbortBanner aborts={aborts} />
        </div>
      ) : null}

      {notice ? (
        <div
          className="mx-6 mt-3 rounded-md border bg-muted px-4 py-2 text-sm"
          role="status"
          data-testid="dunning-review-notice"
        >
          {notice}
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-auto px-6 py-4">
        {error ? (
          <div className="text-sm text-destructive" data-testid="dunning-review-error">
            {`Could not load the review queue: ${error.message}`}
          </div>
        ) : loading && combined.length === 0 ? (
          <div className="text-sm text-muted-foreground">{'Loading…'}</div>
        ) : combined.length === 0 ? (
          <div className="text-sm text-muted-foreground" data-testid="dunning-review-empty">
            {'No accounts are awaiting a decision or deactivated.'}
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <DunningFilterBar value={filters} onChange={setFilters} />
            <PagerSummary page={page} testId="dunning-review" />
            <div className="overflow-x-auto rounded-md border">
              <Table className={DATA_TABLE_CELLS} data-testid="dunning-review-table">
                <TableHeader className={DATA_TABLE_HEADER}>
                  <TableRow>
                    <TableHead>{'Account'}</TableHead>
                    <TableHead>{'Stage'}</TableHead>
                    <TableHead>{'Billing'}</TableHead>
                    <TableHead className="text-right">{'Screens'}</TableHead>
                    <TableHead className="text-right">{'Days past due'}</TableHead>
                    <TableHead>{'Billing record'}</TableHead>
                    <TableHead className="whitespace-nowrap">{'Account status'}</TableHead>
                    <TableHead className="whitespace-nowrap">{'Subscription status'}</TableHead>
                    <TableHead className="whitespace-nowrap">{'Dunning status'}</TableHead>
                    <TableHead className="text-right">{'Decision'}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {page.pageRows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={10}
                        className="py-6 text-center text-sm text-muted-foreground"
                        data-testid="dunning-review-no-match"
                      >
                        {'No account matches these filters.'}
                      </TableCell>
                    </TableRow>
                  ) : null}
                  {page.pageRows.map(({ row, stage }) => (
                    <DunningRow
                      key={row.accountId}
                      row={row}
                      stage={stage}
                      busy={(deciding || reactivating) && pending === row.accountId}
                      onDecide={() => handleDecide(row)}
                      onReactivate={() => setReactivatingRow(row)}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
            <PagerNav page={page} testId="dunning-review" />
          </div>
        )}

        {/*
          The Mongo "Excluded from dunning" list (accountId + expiry, the one staff-app's own
          ladder reads) was REMOVED from this page 2026-09-23 at the requester's direction —
          exclusions are now set in the Billing Console, in Stripe, and listed here. Both
          environments held zero rows when it came off. Its GraphQL ops and the ladder's check
          are untouched.
        */}
        <StripeExclusionsSection />

        <DeactivateDialog
          mode="reactivate"
          row={reactivatingRow}
          onClose={() => setReactivatingRow(null)}
          onConfirm={confirmReactivate}
        />
        <DeactivateDialog
          row={confirming}
          onClose={() => setConfirming(null)}
          onConfirm={confirmDeactivate}
        />
      </div>
    </div>
  );
}
